"use client";

import { type MouseEvent, useCallback } from "react";
import sanitizeHtml from "sanitize-html";
import { cn } from "../../lib/utils";
import styles from "./DangerousText.module.css";

function isInternalLink(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return (
      url.hostname === "localhost" ||
      url.hostname.endsWith(".documentcrunch.com") ||
      url.hostname === "documentcrunch.com"
    );
  } catch {
    return false;
  }
}

export type DangerousTextProps = {
  html: string;
  className?: string;
  /** Called for internal link clicks instead of a full page reload. Receives the path to navigate to. */
  onNavigate?: (path: string) => void;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "dangerouslySetInnerHTML" | "children">;

/**
 * Convert Quill's bullet list format (`<ol><li data-list="bullet">`) into standard `<ul><li>`.
 * Quill renders all lists as `<ol>` and differentiates via `data-list` attributes,
 * but without Quill's CSS these won't display as bullets.
 */
function normalizeQuillLists(input: string): string {
  // Remove Quill's UI marker spans: <span class="ql-ui" contenteditable="false"></span>
  let result = input.replace(/<span\s+class="ql-ui"[^>]*><\/span>/g, "");

  // Convert <ol> containing only bullet items into <ul>
  // Match <ol> blocks and check if all <li> have data-list="bullet"
  result = result.replace(/<ol>([\s\S]*?)<\/ol>/g, (_match, inner: string) => {
    const hasBullets = inner.includes('data-list="bullet"');
    if (hasBullets) {
      // Strip data-list attributes from <li> tags
      const cleanedInner = inner.replace(/\s*data-list="[^"]*"/g, "");
      return `<ul>${cleanedInner}</ul>`;
    }
    return `<ol>${inner}</ol>`;
  });

  return result;
}

export const DangerousText = ({ html, className, onNavigate, ...props }: DangerousTextProps) => {
  const normalizedHtml = normalizeQuillLists(html);
  const sanitizedHtml = sanitizeHtml(normalizedHtml, {
    allowedTags: ["p", "br", "strong", "em", "b", "i", "u", "s", "a", "ul", "ol", "li", "blockquote", "div", "span"],
    allowedAttributes: {
      a: ["href", "target", "rel"],
    },
    allowedSchemes: ["http", "https"],
  });

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor?.href || !isInternalLink(anchor.href)) return;

      e.preventDefault();
      const url = new URL(anchor.href);
      const path = url.pathname + url.search + url.hash;

      if (onNavigate) {
        onNavigate(path);
      } else {
        window.location.href = path;
      }
    },
    [onNavigate],
  );

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: click handler intercepts internal anchor navigation only
    // biome-ignore lint/a11y/noStaticElementInteractions: click handler intercepts internal anchor navigation only
    <div
      className={cn(
        styles.dangerousText,
        "wrap-break-word prose prose-sm max-w-none [&>ol]:my-2 [&>p]:my-2 [&>ul]:my-2",
        className,
      )}
      onClick={handleClick}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is sanitized via sanitize-html before rendering
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      {...props}
    />
  );
};
